import { TransactionDetailSectionProps } from '@/components/DataReflection/TransactionDetailSection/types';
import { useReferenceInvoiceTransactions } from '@/context/services/reportsType/invoiceReports/Edit.service';
import { ContentHeaderProps } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/TransactionDetailsDrawer/components/Contents/ContentHeader';
import { PROGRESS_STEP } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/constants';
import { useDataContext } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/context/UtilizePastDetailsDataContext';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { InlineNotificationProps } from '@moneyforward/ap-frontend-components';
import { InvoiceTransactionDetail } from 'ap-openapi';
import { useEffect, useMemo } from 'react';

export function useTransactionDetailsDrawer(invoiceReportId?: string) {
  const { currentStep, setIsLoading, setNotificationDrawer, onSetCurrentStep } =
    useDataContext();
  const { data, isLoading, error } = useReferenceInvoiceTransactions(
    invoiceReportId ?? '',
    currentStep === PROGRESS_STEP.ON_SELECT_INVOICE
  );
  const { t } = useTranslation();

  const detailFields = useMemo(() => {
    return [
      {
        key: 'deal_date',
        label: '取引日', // translate: 'deal_date'
      },
      {
        key: 'name',
        label: '品目', // translate: 'name'
      },
      {
        key: 'dept',
        label: '費用負担部門', // translate: 'dept'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction.dept?.name ?? '',
      },
      {
        key: 'ex_item',
        label: '経費科目', // translate: 'ex_item'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction.ex_item?.name ?? '',
      },
      {
        key: 'total_value',
        label: '金額（税抜）', // translate: 'total_value'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction.total_value !== undefined
            ? toCommaSeparated(transaction?.total_value)
            : '',
      },
      {
        key: 'dr_excise',
        label: '税区分', // translate: 'excise_name'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction.dr_excise?.name ?? '',
      },
      {
        key: 'excise_value',
        label: '消費税額', // translate: 'tax'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction?.excise_value !== undefined
            ? toCommaSeparated(transaction?.excise_value)
            : '',
      },
      {
        key: 'withholding_income_tax_value',
        label: '源泉徴収税額', // translate: 'withholding_income_tax_value'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction.has_withholding_income_tax &&
          transaction?.withholding_income_tax_value !== undefined
            ? toCommaSeparated(transaction?.withholding_income_tax_value)
            : '',
      },
      {
        key: 'project_code',
        label: 'プロジェクト', // translate: 'project'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction.project_code?.name ?? '',
      },
      {
        key: 'cr_item',
        label: '貸方科目', // translate: 'cr_item'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction.cr_item?.name ?? '',
      },
      {
        key: 'cr_sub_item',
        label: '貸方補助科目', // translate: 'cr_item'
        compute: (transaction: InvoiceTransactionDetail) =>
          transaction.cr_sub_item?.name ?? '',
      },
      {
        key: 'currency',
        label: '通貨', // translate: 'currency'
      },
    ];
  }, []);

  const { detailProps, contentDetailProps } = useMemo(() => {
    const res = data?.data;
    let detailProps: TransactionDetailSectionProps | undefined;
    let contentDetailProps: ContentHeaderProps | undefined;
    if (res) {
      detailProps = {
        isSelected: true,
        transactionList: res?.invoice_transactions,
        fields: detailFields,
        warningMessage: {
          enabled: true,
          message:
            '作成済みの明細は破棄され、選択している申請の明細が反映されます。',
        },
      };
      contentDetailProps = {
        content: {
          href: res?.detail_invoice_report_path ?? '',
          textLink: res?.number_without_prefix,
          title: res?.title,
        },
      };
    }
    return { detailProps, contentDetailProps };
  }, [data, detailFields]);

  const notification: InlineNotificationProps = useMemo(
    () => ({
      visible: isCommonError(error ?? {}),
      type: 'error',
      message:
        isCommonError(error ?? {}) && Array.isArray(error?.messages)
          ? error?.messages.join('')
          : String(error?.messages) ?? t('system_error'),
    }),
    [error, t]
  );
  // Execute side effects declaratively
  useEffect(() => {
    // Set loading state
    setIsLoading(isLoading);

    // Handle step transitions
    if (currentStep === PROGRESS_STEP.ON_SELECT_INVOICE) {
      if (detailProps && !error) {
        onSetCurrentStep(PROGRESS_STEP.ON_OPEN_DRAWER);
      } else if (error && notification.visible) {
        setNotificationDrawer(notification);
        onSetCurrentStep(PROGRESS_STEP.ON_OPEN_DIALOG);
      } else {
        setNotificationDrawer(null);
      }
    }
  }, [
    isLoading,
    notification,
    detailProps,
    currentStep,
    setIsLoading,
    error,
    setNotificationDrawer,
    onSetCurrentStep,
  ]);

  return {
    detailProps,
    isLoading,
    contentDetailProps,
    notification,
  };
}
